import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChefHat, ShoppingBag, Truck, ArrowRight, UtensilsCrossed } from 'lucide-react';
import './HeroSection.css';

interface HeroSectionProps {
  userRoles?: string[];
  onSellFood?: () => void;
  onDeliveryPartner?: () => void;
}

type ButtonVariant = 'primary' | 'secondary' | 'ghost';

interface CtaButton {
  label: string;
  variant: ButtonVariant;
  icon: React.ReactNode;
  action: 'sell_food' | 'delivery_partner' | 'browse_meals' | 'my_kitchen' | 'my_orders' | 'my_deliveries';
}

interface HeroConfig {
  title: string;
  subtitle: string;
  bgClass: string;
  buttons: CtaButton[];
}

function getRoleKey(roles: string[]): string {
  return [...roles].sort().join('|');
}

function buildConfig(roles: string[]): HeroConfig {
  const isC = roles.includes('customer');
  const isK = roles.includes('kitchen_owner');
  const isD = roles.includes('delivery_partner');
  const key = getRoleKey(roles);

  const configs: Record<string, HeroConfig> = {
    // Customer only
    'customer': {
      title: 'Hungry? Great food is nearby.',
      subtitle: 'Discover home-cooked meals made with love by talented chefs in your neighborhood. Fresh, affordable, and authentic.',
      bgClass: 'hero-bg--food',
      buttons: [
        { label: 'Browse Meals', variant: 'primary', icon: <ShoppingBag size={20} />, action: 'browse_meals' },
        { label: 'My Orders', variant: 'secondary', icon: <ArrowRight size={18} />, action: 'my_orders' },
      ],
    },
    // Kitchen Owner only
    'kitchen_owner': {
      title: 'Cook. Share. Earn.',
      subtitle: 'Turn your home kitchen into a thriving business. List your meals, set your prices, and start receiving orders today.',
      bgClass: 'hero-bg--kitchen',
      buttons: [
        { label: 'Start Selling Food', variant: 'primary', icon: <ChefHat size={20} />, action: 'sell_food' },
        { label: 'My Kitchen', variant: 'secondary', icon: <UtensilsCrossed size={18} />, action: 'my_kitchen' },
      ],
    },
    // Delivery Partner only
    'delivery_partner': {
      title: 'Pick up. Drop off. Earn.',
      subtitle: 'Deliver home-cooked meals from local chefs to hungry neighbors. Work on your own schedule and earn every delivery.',
      bgClass: 'hero-bg--delivery',
      buttons: [
        { label: 'Manage Deliveries', variant: 'primary', icon: <Truck size={20} />, action: 'my_deliveries' },
        { label: 'Delivery Partner Info', variant: 'secondary', icon: <ArrowRight size={18} />, action: 'delivery_partner' },
      ],
    },
    // Customer + Kitchen Owner
    'customer|kitchen_owner': {
      title: 'Order food or sell your cooking.',
      subtitle: "You're both a food lover and a home chef. Browse delicious meals around you — or open your kitchen and start earning.",
      bgClass: 'hero-bg--kitchen',
      buttons: [
        { label: 'Start Selling Food', variant: 'primary', icon: <ChefHat size={20} />, action: 'sell_food' },
        { label: 'Browse Meals', variant: 'secondary', icon: <ShoppingBag size={18} />, action: 'browse_meals' },
        { label: 'My Kitchen', variant: 'ghost', icon: <UtensilsCrossed size={16} />, action: 'my_kitchen' },
      ],
    },
    // Customer + Delivery Partner
    'customer|delivery_partner': {
      title: 'Order meals or earn on the road.',
      subtitle: 'Browse home-cooked food nearby or pick up a delivery shift — two ways to enjoy the HomeCookt community.',
      bgClass: 'hero-bg--food',
      buttons: [
        { label: 'Browse Meals', variant: 'primary', icon: <ShoppingBag size={20} />, action: 'browse_meals' },
        { label: 'Manage Deliveries', variant: 'secondary', icon: <Truck size={18} />, action: 'my_deliveries' },
      ],
    },
    // Kitchen Owner + Delivery Partner
    'delivery_partner|kitchen_owner': {
      title: 'Cook it. Deliver it. Earn more.',
      subtitle: 'Sell your home-cooked meals and take on delivery assignments — two powerful ways to earn on a single platform.',
      bgClass: 'hero-bg--kitchen',
      buttons: [
        { label: 'Start Selling Food', variant: 'primary', icon: <ChefHat size={20} />, action: 'sell_food' },
        { label: 'Manage Deliveries', variant: 'secondary', icon: <Truck size={18} />, action: 'my_deliveries' },
        { label: 'My Kitchen', variant: 'ghost', icon: <UtensilsCrossed size={16} />, action: 'my_kitchen' },
      ],
    },
    // All Three
    'customer|delivery_partner|kitchen_owner': {
      title: 'Welcome to your HomeCookt hub.',
      subtitle: 'Order food, run your kitchen, and make deliveries — everything you need in one place.',
      bgClass: 'hero-bg--kitchen',
      buttons: [
        { label: 'Start Selling Food', variant: 'primary', icon: <ChefHat size={20} />, action: 'sell_food' },
        { label: 'Browse Meals', variant: 'secondary', icon: <ShoppingBag size={18} />, action: 'browse_meals' },
        { label: 'Manage Deliveries', variant: 'ghost', icon: <Truck size={16} />, action: 'my_deliveries' },
      ],
    },
  };

  return configs[key] ?? {
    title: 'Welcome to HomeCookt.',
    subtitle: 'Connecting food lovers with talented home cooks in your neighborhood.',
    bgClass: 'hero-bg--food',
    buttons: [
      { label: 'Browse Meals', variant: 'primary', icon: <ShoppingBag size={20} />, action: 'browse_meals' },
    ],
  };
}

const HeroSection: React.FC<HeroSectionProps> = ({ userRoles = [], onSellFood, onDeliveryPartner }) => {
  const navigate = useNavigate();
  const config = buildConfig(userRoles);

  const handleAction = (action: CtaButton['action']) => {
    switch (action) {
      case 'sell_food':
        onSellFood?.();
        break;
      case 'delivery_partner':
        onDeliveryPartner?.();
        break;
      case 'browse_meals':
        document.getElementById('featured-meals')?.scrollIntoView({ behavior: 'smooth' });
        break;
      case 'my_kitchen':
        navigate('/profile?tab=kitchen');
        break;
      case 'my_orders':
        navigate('/profile?tab=my-orders');
        break;
      case 'my_deliveries':
        navigate('/profile?tab=my-deliveries');
        break;
    }
  };

  const roleCount = [
    userRoles.includes('customer'),
    userRoles.includes('kitchen_owner'),
    userRoles.includes('delivery_partner'),
  ].filter(Boolean).length;

  const roleBadges = [];
  if (userRoles.includes('customer')) roleBadges.push({ label: 'Customer', icon: <ShoppingBag size={13} />, cls: 'badge--customer' });
  if (userRoles.includes('kitchen_owner')) roleBadges.push({ label: 'Kitchen Owner', icon: <ChefHat size={13} />, cls: 'badge--kitchen' });
  if (userRoles.includes('delivery_partner')) roleBadges.push({ label: 'Delivery Partner', icon: <Truck size={13} />, cls: 'badge--delivery' });

  return (
    <section className={`hero-container ${roleCount >= 2 ? 'hero-container--tall' : ''}`}>
      <div className={`hero-image ${config.bgClass}`} />
      <div className="hero-content">
        {roleBadges.length > 0 && (
          <div className="hero-badges">
            {roleBadges.map((b) => (
              <span key={b.label} className={`hero-badge ${b.cls}`}>
                {b.icon}
                {b.label}
              </span>
            ))}
          </div>
        )}
        <h1 className="hero-title">{config.title}</h1>
        <p className="hero-subtitle">{config.subtitle}</p>
        <div className={`hero-cta-row ${config.buttons.length >= 3 ? 'hero-cta-row--three' : ''}`}>
          {config.buttons.map((btn) => (
            <button
              key={btn.action}
              className={`hero-btn hero-btn--${btn.variant}`}
              onClick={() => handleAction(btn.action)}
            >
              {btn.icon}
              {btn.label}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
